import { useQuery } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';

// ================================
// TRANSFER TYPES
// ================================

export interface TransferLineItem {
  _id: string;
  ingredientId: string | {
    _id: string;
    name: { en: string; th?: string };
    sku?: string;
    unit?: string;
  };
  // Quantities
  requestedQuantity: number;
  approvedQuantity?: number;
  dispatchedQuantity?: number;
  receivedQuantity?: number;
  rejectedQuantity?: number;
  unit: string;
  // Batch tracking - FEFO support
  batchNumber?: string;
  supplierBatchNumber?: string;
  expiryDate?: string;
  // FEFO selection tracking
  selectedByFEFO?: boolean;
  fefoOverrideReason?: string;
  fefoOverrideBy?: { _id: string; username: string; email: string };
  // Stock reservation
  isReserved?: boolean;
  reservedAt?: string;
  reservedQuantity?: number;
  // Dispatch verification
  dispatchVerified?: boolean;
  dispatchVerifiedAt?: string;
  dispatchVerifiedBy?: { _id: string; username: string; email: string };
  dispatchScanData?: {
    scannedBarcode: string;
    scanType: 'supplier_barcode' | 'internal_qr' | 'manual';
    scanTimestamp: string;
  };
  // Receiving
  receivingCondition?: 'good' | 'damaged' | 'expired' | 'wrong_item' | 'quantity_short' | 'quality_issue';
  // Rejection handling
  isRejected?: boolean;
  rejectionReason?: string;
  rejectionPhotoUrls?: string[];
  rejectedBy?: { _id: string; username: string; email: string };
  rejectedAt?: string;
  // Replacement tracking
  replacementTransferId?: string;
  notes?: string;
}

export interface SLATracking {
  expectedDeliveryHours?: number;
  slaDeadline?: string;
  isEscalated?: boolean;
  escalationLevel?: number;
  escalatedAt?: string;
  escalationNotifiedUsers?: string[];
  slaStatus?: 'on_track' | 'at_risk' | 'breached';
  hoursRemaining?: number;
  hoursOverdue?: number;
}

export interface RejectionSummary {
  totalRejected: number;
  rejectionReasons: string[];
  requiresReplacement: boolean;
  replacementTransferId?: string;
}

export interface Transfer {
  _id: string;
  transferNumber: string;
  type: 'warehouse_to_branch' | 'branch_to_branch' | 'branch_to_warehouse';
  status:
    | 'request'
    | 'pending_hq_approval'
    | 'approved'
    | 'picking'
    | 'pending_dispatch_verification'
    | 'in_transit'
    | 'pending_receiving'
    | 'partially_received'
    | 'received'
    | 'completed'
    | 'cancelled'
    | 'rejected';

  // Location info
  fromLocationId: string | { _id: string; name: string; code: string; address?: string };
  toLocationId: string | { _id: string; name: string; code: string; address?: string };
  items: TransferLineItem[];

  // Branch-to-branch HQ approval
  requiresHQApproval?: boolean;
  hqApprovalStatus?: 'not_required' | 'pending' | 'approved' | 'rejected';
  hqApprovedBy?: { _id: string; username: string; email: string };
  hqApprovedAt?: string;
  hqRejectionReason?: string;

  // Stock reservation
  stockReserved?: boolean;
  stockReservedAt?: string;
  stockReservedBy?: { _id: string; username: string; email: string };
  reservationExpiresAt?: string;

  // User tracking
  requestedBy: string | { _id: string; username: string; email: string };
  approvedBy?: string | { _id: string; username: string; email: string };
  pickedBy?: string | { _id: string; username: string; email: string };
  dispatchedBy?: string | { _id: string; username: string; email: string };
  receivedBy?: string | { _id: string; username: string; email: string };

  // Date tracking
  requestedDate: string;
  approvedDate?: string;
  pickingStartedAt?: string;
  pickingCompletedAt?: string;
  dispatchDate?: string;
  expectedDeliveryDate?: string;
  actualDeliveryDate?: string;
  receivedDate?: string;
  completedDate?: string;

  // SLA tracking
  slaTracking?: SLATracking;

  // Dispatch verification
  dispatchVerificationRequired?: boolean;
  dispatchFullyVerified?: boolean;
  dispatchVerificationCompletedAt?: string;

  // Receiving summary
  hasRejectedItems?: boolean;
  rejectionSummary?: RejectionSummary;

  // Auto-replacement tracking
  isReplacementTransfer?: boolean;
  originalTransferId?: string | { _id: string; transferNumber: string };

  // Notes
  rejectionReason?: string;
  notes?: string;
  internalNotes?: string;

  // Photo documentation
  dispatchPhotos?: string[];
  receivingPhotos?: string[];

  createdAt: string;
  updatedAt: string;
}

export interface Branch {
  _id: string;
  name: string;
  code: string;
  isWarehouse?: boolean;
  isActive: boolean;
  address?: string;
}

export interface TransfersResponse {
  transfers: Transfer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface SLADashboard {
  summary: {
    onTrack: number;
    atRisk: number;
    breached: number;
    total: number;
  };
  urgentTransfers: Transfer[];
}

export interface TransferAnalytics {
  statusSummary: Record<string, number>;
  typeSummary: Record<string, number>;
  slaSummary: {
    total: number;
    onTime: number;
    breached: number;
  };
  rejectionSummary: {
    transfersWithRejections: number;
    totalRejectedItems: number;
  };
}

// ================================
// QUERY HOOKS
// ================================

/**
 * Fetch all transfers with optional filters
 */
export function useTransfers(options?: {
  fromLocationId?: string;
  toLocationId?: string;
  status?: string;
  type?: string;
  requiresHQApproval?: boolean;
  slaStatus?: string;
  page?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: ['transfers', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.fromLocationId) params.append('fromLocationId', options.fromLocationId);
      if (options?.toLocationId) params.append('toLocationId', options.toLocationId);
      if (options?.status) params.append('status', options.status);
      if (options?.type) params.append('type', options.type);
      if (options?.requiresHQApproval) params.append('requiresHQApproval', 'true');
      if (options?.slaStatus) params.append('slaStatus', options.slaStatus);
      if (options?.page) params.append('page', options.page.toString());
      if (options?.limit) params.append('limit', options.limit.toString());

      const response = await apiClient.get<APIResponse<TransfersResponse>>(
        `/transfers?${params.toString()}`
      );
      return response.data.data;
    },
  });
}

/**
 * Fetch single transfer by ID
 */
export function useTransfer(id: string) {
  return useQuery({
    queryKey: ['transfer', id],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ transfer: Transfer }>>(
        `/transfers/${id}`
      );
      return response.data.data.transfer;
    },
    enabled: !!id,
  });
}

/**
 * Fetch transfers pending HQ approval (branch-to-branch)
 */
export function usePendingHQApprovalTransfers() {
  return useQuery({
    queryKey: ['transfers', 'pending-hq-approval'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ transfers: Transfer[] }>>(
        '/transfers/pending-hq-approval'
      );
      return response.data.data.transfers;
    },
  });
}

/**
 * Fetch SLA dashboard data
 */
export function useSLADashboard() {
  return useQuery({
    queryKey: ['transfers', 'sla-dashboard'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<SLADashboard>>(
        '/transfers/sla-dashboard'
      );
      return response.data.data;
    },
    refetchInterval: 60000, // Refresh every minute
  });
}

/**
 * Fetch transfer analytics/summary
 */
export function useTransferAnalytics(options?: {
  startDate?: string;
  endDate?: string;
  locationId?: string;
}) {
  return useQuery({
    queryKey: ['transfers', 'analytics', options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.startDate) params.append('startDate', options.startDate);
      if (options?.endDate) params.append('endDate', options.endDate);
      if (options?.locationId) params.append('locationId', options.locationId);

      const response = await apiClient.get<APIResponse<TransferAnalytics>>(
        `/transfers/analytics/summary?${params.toString()}`
      );
      return response.data.data;
    },
  });
}

/**
 * Fetch branches/locations for dropdowns
 */
export function useBranches() {
  return useQuery({
    queryKey: ['branches'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ branches: Branch[] }>>(
        '/branches'
      );
      return response.data.data.branches;
    },
  });
}

/**
 * Fetch warehouse only
 */
export function useWarehouse() {
  return useQuery({
    queryKey: ['warehouse'],
    queryFn: async () => {
      const response = await apiClient.get<APIResponse<{ branches: Branch[] }>>(
        '/branches?isWarehouse=true'
      );
      return response.data.data.branches[0] || null;
    },
  });
}

// Export types for use in mutations
export type { Transfer as TransferType };
